import * as _ from 'underscore';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { RequestHelper } from '../helpers/RequestHelper';
import { IImportUsersResult, ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { Module } from '../Module';
import { AddUsersToSiteRequest } from '../ServerApi/requests/AddUsersToSiteRequest';
import { CreateLocalUserRequest } from '../ServerApi/requests/CreateLocalUserRequest';
import { CreateLocalUserWithSiteSamlAuthRequest } from '../ServerApi/requests/CreateLocalUserWithSiteSamlAuthRequest';
import { DeleteUsersRequest } from '../ServerApi/requests/DeleteUsersRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import {
  GetRemainingUserQuotaRequest,
  IGetRemainingUserQuotaResult,
} from '../ServerApi/requests/GetRemainingUserQuotaRequest';
import { GetSiteUsersRequest } from '../ServerApi/requests/GetSiteUsersRequest';
import { ImportExternalSiteUsersRequest } from '../ServerApi/requests/ImportExternalUsers';
import { ImportSiteUsersFromActiveDirectoryRequest } from '../ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { UpdateUsersAdminStatusRequest } from '../ServerApi/requests/UpdateUsersAdminStatusRequest';
import { UpdateUsersGroupMembershipRequest } from '../ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { UpdateUsersSiteRoleRequest } from '../ServerApi/requests/UpdateUsersSiteRoleRequest';
import { FilterFieldNames, IFieldFilterClause, IFilterClause } from '../ServerApi/types/IFilterClause';
import { IPagedItemsParams } from '../ServerApi/types/IPage';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { IUserActionResult } from '../ServerApi/types/IUserActionResult';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { Resource } from './Resource';
import { IResourceQuery } from './ResourceQuery';
import { SiteRolesResource } from './SiteRoles';

// populates the authenticationProviderId field of ISiteUser (TODO: move server-side and remove this class completely, TFSID:607630)
class AuthenticationProviderIdFetcher implements IListFetcher<ISiteUserInfo> {
  private siteUserFetcher: IListFetcher<ISiteUserInfo>;

  constructor(
    private $q: ng.IQService,
    fetcherFactory: FetcherFactory,
    private server: ServerService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService
  ) {
    this.siteUserFetcher = fetcherFactory.getServerResourceFetcher(GetSiteUsersRequest, ServerApiMapper.siteUsers);
  }

  public fetchList(params: IPagedItemsParams): ng.IPromise<ListResult<ISiteUserInfo>> {
    if (!this.siteAuthenticationSettingsService.isEnabled()) {
      return this.siteUserFetcher.fetchList(params);
    }

    return this.$q.all([
      this.siteUserFetcher.fetchList(params),
      this.siteAuthenticationSettingsService.getAuthSettingToProviderIdMap()])
    .then((results: any[]) => {
      const userList: ListResult<ISiteUserInfo> = results[0];
      const map: _.Dictionary<string> = results[1];

      _.each(userList.items, (siteUser: ISiteUserInfo) => {
        siteUser.authenticationProviderId = map[siteUser.authSetting];
      });
      return userList;
    });
  }
}

export class SiteUsers extends Resource<ISiteUserInfo> {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'FetcherFactory',
    'ServerService',
    'SiteRoles',
    'SiteAuthenticationSettingsService'
  ];

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    fetcherFactory: FetcherFactory,
    private server: ServerService,
    private siteRoles: SiteRolesResource,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService
  ) {
    super(new AuthenticationProviderIdFetcher($q, fetcherFactory, server, siteAuthenticationSettingsService));
  }

  // gets the user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): ng.IPromise<any> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  public getUsersById(userIds: string[]): ng.IPromise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray('id', userIds)));
  }

  // gets users with the specified siteRoles.
  public getUsersBySiteRoles(siteRoles: string[]): ng.IPromise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray(FilterFieldNames.siteRole, siteRoles)));
  }

  // group membership - remove only
  public removeFromGroups(userIds: string[], groupIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: [],
          removeFromGroupIds: groupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  public getUserCountsBySiteRole(baseQuery: IResourceQuery<ISiteUserInfo>): ng.IPromise<_.Dictionary<number>> {

    // remove any existing site role filter
    const filtersWithoutSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.siteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutSiteRole);

    const counts: _.Dictionary<number> = {};

    const allSiteRolesExceptGuest = _.without(this.siteRoles.allOptions(), SiteRoles.Guest);

    const promises = _.map(allSiteRolesExceptGuest, (siteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
        .getCount()
        .then((totalCount: number) => {
          counts[siteRole] = totalCount;
        });
    });

    //Any site role
    promises.push(modifiedQuery.getCount().then((totalCount: number) => {counts['Any'] = totalCount; }));

    return this.$q.all(promises).then(() => counts);
  }

  // group membership - add only
  public addToGroups(userIds: string[], groupIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: groupIdsBlock,
          removeFromGroupIds: []
        })),
      ResultHelper.combineResults);
  }

  // group membership - add and remove
  public updateUsersGroupMembership(userIds: string[], addToGroupIds: string[], removeFromGroupIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToGroupIds, removeFromGroupIds,
        (userIdsBlock, addToGroupIdsBlock, removeFromGroupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: addToGroupIdsBlock,
          removeFromGroupIds: removeFromGroupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  // creates a local user
  public createLocalUser(
    username: string,
    displayName: string,
    email: string,
    password: string,
    admin: boolean,
    siteRole: string
  ): ng.IPromise<IUserActionResult> {
    return this.server.sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalUserRequest = new CreateLocalUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId,
          admin: admin,
          siteRole: siteRole
        });
        return this.ErrorNotifyingServer.sendRequestAndDelay(createLocalUserRequest);
      });
  }

  // creates a local SiteSAML user
  public createLocalUserWithSamlOrSiteSamlAuth(
    username: string,
    displayName: string,
    email: string,
    authenticationProviderId: string,
    admin: boolean,
    siteRole: string
  ): ng.IPromise<IUserActionResult> {
    return this.siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = this.siteAuthenticationSettingsService.getAuthSetting(authenticationProviderId, settings);
      const createLocalUserWithSiteSamlAuthRequest = new CreateLocalUserWithSiteSamlAuthRequest({
            username: username,
            displayName: displayName,
            email: email,
            authSetting: authSetting,
            admin: admin,
            siteRole: siteRole
          });
      return this.ErrorNotifyingServer.sendRequestAndDelay(createLocalUserWithSiteSamlAuthRequest);
    });
  }

  // adds users to this site
  public addUsersToSite(usernames: string[], siteRole: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames, (usernamesBlock) => new AddUsersToSiteRequest(usernamesBlock, siteRole)),
      ResultHelper.combineResults);
  }

  // delete users from this site
  public deleteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteUsersRequest(userIdsBlock)),
      ResultHelper.combineUserResults);
  }

  // imports users from active directory to this site
  public importUsersFromActiveDirectory(userIds: string[], siteRole: string, serverAdmin: boolean = false): ng.IPromise<IUserActionResult> {
    let requestBuilder: (userIds: string[]) => ImportSiteUsersFromActiveDirectoryRequest;
    if (serverAdmin) {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, admin: true });
    } else {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, siteRole: siteRole });
    }
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, requestBuilder),
      ResultHelper.combineUserResults);
  }

  public importExternalUsers(emails: string[], siteRole: string, authenticationProviderId: string): ng.IPromise<IUserActionResult> {
    return this.siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = this.siteAuthenticationSettingsService.getAuthSetting(authenticationProviderId, settings);
      return this.ErrorNotifyingServer.sendRequestsAndDelay(
        RequestHelper.buildRequests(emails, (emailsBlock) => new ImportExternalSiteUsersRequest({
          emailAddresses: emailsBlock,
          siteRole: siteRole,
          authSetting: authSetting
        })),
        ResultHelper.combineUserResults);
    });
  }

  // updates users site role for this site
  public updateUsersSiteRole(userIds: string[], siteRole: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersSiteRoleRequest({
        userIds: userIdsBlock,
        siteRole: siteRole
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. Includes SOLR delay.
  public updateServerAdminStatusAndDelay(userIds: string[], admin: boolean): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. No SOLR delay.
  public updateServerAdminStatusNoDelay(userIds: string[], admin: boolean): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  public getRemainingUserQuota(): ng.IPromise<number> {
    return this.server.sendRequest(new GetRemainingUserQuotaRequest())
      .then((result: IGetRemainingUserQuotaResult) => result.usersRemaining);
  }

}

Module.instance().service('SiteUsers', SiteUsers);
